import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash, UserPlus, Crown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import useDirectory from '@/hooks/useDirectory';
import LicenseSharePicker from './LicenseSharePicker';
import { itApi } from './itApi';

const EMPTY = {
  name: '', vendor: '', license_key: '', license_type: 'subscription',
  seats_total: '', purchase_date: '', cost: '', expiry_date: '',
  login_email: '', password: '', notes: '',
  shared_employee_ids: [], shared_department_ids: [],
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs font-semibold text-zinc-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function LicenseFormDialog({ open, onClose, license, onSaved }) {
  const editing = !!license;
  const { employees, departments } = useDirectory();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [seats, setSeats] = useState([]);
  const [seatEdits, setSeatEdits] = useState({}); // { seatId: { login_email, password } }
  const [assets, setAssets] = useState([]);
  const [targetType, setTargetType] = useState('employee');
  const [targetId, setTargetId] = useState('');
  const [seatBusy, setSeatBusy] = useState(false);

  const loadSeats = () => {
    if (license) itApi.licenseSeats(license.id).then(setSeats).catch(() => {});
  };

  useEffect(() => {
    if (open) {
      setForm(license
        ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, license[k] ?? EMPTY[k]]))
        : EMPTY);
      setSeats([]); setSeatEdits({}); setTargetId(''); setTargetType('employee');
      if (license) {
        itApi.licenseSeats(license.id).then(setSeats).catch(() => {});
        itApi.assets().then(setAssets).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, license]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const setSeat = (sid, k) => (e) => setSeatEdits((p) => ({ ...p, [sid]: { ...p[sid], [k]: e.target.value } }));

  const addSeat = async () => {
    if (!targetId) { toast.error('割当先を選択してください'); return; }
    setSeatBusy(true);
    try {
      await itApi.assignSeat(license.id, { target_type: targetType, target_id: targetId });
      toast.success('割り当てました'); setTargetId('');
      loadSeats(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || '割当に失敗しました'); }
    finally { setSeatBusy(false); }
  };

  const toggleSeatAdmin = async (s) => {
    setSeatBusy(true);
    try {
      await itApi.updateSeat(license.id, s.id, { is_admin: !s.is_admin });
      toast.success(s.is_admin ? '管理者を解除しました' : '管理者に設定しました');
      loadSeats(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || '更新に失敗しました'); }
    finally { setSeatBusy(false); }
  };

  const releaseSeat = async (seatId) => {
    setSeatBusy(true);
    try {
      await itApi.releaseSeat(license.id, seatId);
      toast.success('解除しました');
      loadSeats(); onSaved?.();
    } catch { toast.error('解除に失敗しました'); } finally { setSeatBusy(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('ソフト名を入力してください'); return; }
    setSaving(true);
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const payload = {
      ...form, name: form.name.trim(),
      seats_total: num(form.seats_total), cost: num(form.cost),
    };
    Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
    try {
      if (editing) {
        await itApi.updateLicense(license.id, payload);
        // 変更のあった座席の認証情報も一緒に保存
        for (const s of seats) {
          const ed = seatEdits[s.id];
          if (!ed) continue;
          const emailChanged = ed.login_email !== undefined && ed.login_email !== (s.login_email || '');
          const pwEntered = !!ed.password;
          if (!emailChanged && !pwEntered) continue;
          const body = {};
          if (ed.login_email !== undefined) body.login_email = ed.login_email || null;
          if (pwEntered) body.password = ed.password;
          await itApi.updateSeat(license.id, s.id, body);
        }
      } else {
        await itApi.createLicense(payload);
      }
      toast.success(editing ? 'ライセンスを更新しました' : 'ライセンスを登録しました');
      onSaved?.(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="license-form-dialog">
        <DialogHeader><DialogTitle>{editing ? 'ライセンスを編集' : 'ライセンスを登録'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-1">
          <div className="sm:col-span-2">
            <Field label="ソフト名 *" testid="license-field-name">
              <Input data-testid="license-input-name" value={form.name} onChange={set('name')} placeholder="例: Microsoft 365" className="h-10" />
            </Field>
          </div>
          <Field label="ベンダー" testid="license-field-vendor">
            <Input data-testid="license-input-vendor" value={form.vendor} onChange={set('vendor')} className="h-10" />
          </Field>
          <Field label="種別" testid="license-field-type">
            <Select value={form.license_type} onValueChange={set('license_type')}>
              <SelectTrigger data-testid="license-input-type" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="subscription">サブスク</SelectItem>
                <SelectItem value="perpetual">買い切り</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="ライセンスキー" testid="license-field-key">
              <Input data-testid="license-input-key" value={form.license_key} onChange={set('license_key')} className="h-10 font-mono" />
            </Field>
          </div>
          <Field label="座席数（総数）" testid="license-field-seats">
            <Input data-testid="license-input-seats" type="number" min="0" value={form.seats_total} onChange={set('seats_total')} placeholder="例: 10（空欄=無制限）" className="h-10" />
          </Field>
          <Field label="金額（円）" testid="license-field-cost">
            <Input data-testid="license-input-cost" type="number" min="0" value={form.cost} onChange={set('cost')} className="h-10" />
          </Field>
          <Field label="購入日" testid="license-field-purchase">
            <DateField testId="license-input-purchase" value={(form.purchase_date || '').slice(0, 10)} onChange={set('purchase_date')} inputClassName="h-10" />
          </Field>
          <Field label="更新（有効）期限" testid="license-field-expiry">
            <DateField testId="license-input-expiry" value={(form.expiry_date || '').slice(0, 10)} onChange={set('expiry_date')} inputClassName="h-10" />
          </Field>
          <div className="sm:col-span-2 mt-1 pt-3 border-t border-zinc-200">
            <p className="text-xs font-semibold text-zinc-500 mb-2">ログイン情報（このアカウントにサインインするため）</p>
          </div>
          <Field label="ログイン用メール" testid="license-field-login-email">
            <Input data-testid="license-input-login-email" value={form.login_email} onChange={set('login_email')} placeholder="例: it-admin@sensbey.co.jp" className="h-10" />
          </Field>
          <Field label={editing ? 'パスワード（空欄=変更なし）' : 'パスワード'} testid="license-field-password">
            <Input data-testid="license-input-password" type="text" autoComplete="new-password" value={form.password} onChange={set('password')} placeholder={editing ? '••••••（変更時のみ）' : ''} className="h-10 font-mono" />
          </Field>
          <div className="sm:col-span-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="license-field-sharing">
            <p className="text-xs font-semibold text-zinc-600">共有先（このログイン情報を見られる人）</p>
            <p className="mb-2 mt-0.5 text-[10px] text-zinc-400">
              未選択のままなら、従来どおりIT管理者だけが表示できます。選んだ社員と部署のメンバーは
              このライセンスのメールとパスワードを表示・コピーできます（すべて監査ログに記録されます）。
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <LicenseSharePicker label="社員" placeholder="社員を選択（任意）" options={employees}
                selected={form.shared_employee_ids} testId="license-share-employees"
                onChange={(v) => setForm((p) => ({ ...p, shared_employee_ids: v }))} />
              <LicenseSharePicker label="部署" placeholder="部署を選択（任意）" options={departments}
                selected={form.shared_department_ids} testId="license-share-departments"
                onChange={(v) => setForm((p) => ({ ...p, shared_department_ids: v }))} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Field label="備考" testid="license-field-notes">
              <Textarea data-testid="license-input-notes" value={form.notes} onChange={set('notes')} className="min-h-[60px]" />
            </Field>
          </div>
          {editing && (
            <div className="sm:col-span-2 mt-1 pt-3 border-t border-zinc-200" data-testid="license-form-seat-creds">
              <p className="text-xs font-semibold text-zinc-500">メンバー（座席）と認証情報</p>
              <p className="text-[10px] text-zinc-400 mt-0.5 mb-2">ここでメンバーの追加・解除と、メンバーごとのログインメール・パスワードの編集ができます。社員を追加するとログインメールは社員台帳から自動入力されます（後から編集可能）。パスワードは本人も「マイデバイス」から登録できます。</p>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-2 mb-2" data-testid="license-form-seat-assign">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetId(''); }}>
                    <SelectTrigger data-testid="license-form-seat-type" className="h-9 sm:w-28 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">社員</SelectItem>
                      <SelectItem value="asset">資産</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={targetId} onValueChange={setTargetId}>
                    <SelectTrigger data-testid="license-form-seat-target" className="h-9 flex-1 text-sm"><SelectValue placeholder={targetType === 'employee' ? '社員を選択' : '資産を選択'} /></SelectTrigger>
                    <SelectContent>
                      {(targetType === 'employee' ? employees : assets).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}{o.asset_tag ? `（${o.asset_tag}）` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" data-testid="license-form-seat-add-btn" disabled={seatBusy} onClick={addSeat} className="h-9 bg-teal-700 hover:bg-teal-800">
                    <UserPlus size={15} className="mr-1" /> 追加
                  </Button>
                </div>
              </div>
              {seats.length === 0 ? (
                <p className="text-xs text-zinc-400 text-center py-2" data-testid="license-form-seat-empty">まだメンバーが割り当てられていません</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="license-form-admin-line">
                    <Crown size={12} weight="fill" className="text-amber-500" /> 管理者:{' '}
                    <span className="font-semibold">{seats.filter((s) => s.is_admin).map((s) => s.target_label).join('、') || '未設定'}</span>
                  </p>
                  {[...seats].sort((a, b) => (b.is_admin ? 1 : 0) - (a.is_admin ? 1 : 0)).map((s) => {
                    const ed = seatEdits[s.id] || {};
                    return (
                      <div key={s.id} className="rounded-lg border border-zinc-100 p-2" data-testid={`license-form-seat-${s.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 text-xs font-medium text-zinc-700 min-w-0">
                            <span className="truncate">{s.target_label}</span>
                            {s.is_admin && <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 shrink-0" data-testid={`license-form-seat-admin-badge-${s.id}`}><Crown size={10} weight="fill" /> 管理者</span>}
                            <span className="text-[10px] font-normal text-zinc-400 shrink-0">{s.target_type === 'asset' ? '資産' : 'メンバー'}{s.has_password ? '・パスワード登録済み' : ''}</span>
                          </div>
                          <span className="flex items-center gap-0.5 shrink-0">
                            <Button type="button" size="sm" variant="ghost" data-testid={`license-form-seat-admin-${s.id}`}
                              disabled={seatBusy} onClick={() => toggleSeatAdmin(s)}
                              className={`h-7 px-2 ${s.is_admin ? 'text-amber-600 hover:bg-amber-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
                              title={s.is_admin ? '管理者を解除' : 'このライセンスの管理者にする'}><Crown size={14} weight={s.is_admin ? 'fill' : 'regular'} /></Button>
                            <Button type="button" size="sm" variant="ghost" data-testid={`license-form-seat-release-${s.id}`}
                              disabled={seatBusy} onClick={() => releaseSeat(s.id)}
                              className="h-7 px-2 text-red-600 hover:bg-red-50" title="座席を解除"><Trash size={14} /></Button>
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input value={ed.login_email !== undefined ? ed.login_email : (s.login_email || '')}
                            onChange={setSeat(s.id, 'login_email')} placeholder="ログインメール"
                            data-testid={`license-form-seat-email-${s.id}`} className="h-9 text-sm" />
                          <Input type="text" autoComplete="new-password" value={ed.password || ''}
                            onChange={setSeat(s.id, 'password')}
                            placeholder={s.has_password ? 'パスワード（変更時のみ）' : 'パスワード（任意）'}
                            data-testid={`license-form-seat-password-${s.id}`} className="h-9 text-sm font-mono" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="license-form-cancel">キャンセル</Button>
          <Button data-testid="license-form-submit" disabled={saving} onClick={submit} className="bg-teal-700 hover:bg-teal-800">
            {editing ? '更新する' : '登録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
